import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  LayoutDashboard, 
  PieChart, 
  Calculator, 
  Search, 
  TrendingUp, 
  BookOpen, 
  Settings, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  User,
  Bell,
  Star,
  Shield,
  Calendar,
  Target,
  Award,
  Filter,
  BarChart3,
  LineChart,
  Landmark,
  FileText,
  Building2,
  ShoppingCart,
  RefreshCcw,
  Users,
  Upload
} from "lucide-react";

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/portfolio", label: "Portfolio", icon: PieChart },
    { to: "/watchlist", label: "Watchlist", icon: Star },
    { to: "/alerts", label: "Price Alerts", icon: Target },
    { to: "/sip-reminders", label: "SIP Reminders", icon: Calendar },
    { divider: true, label: "BSE StARMF" },
    { to: "/distributor", label: "Dashboard", icon: Building2 },
    { to: "/distributor/clients", label: "Client Portfolios", icon: Users },
    { to: "/distributor/transactions", label: "Place Order", icon: ShoppingCart },
    { to: "/distributor/sip", label: "SIP Management", icon: RefreshCcw },
    { to: "/distributor/cas-import", label: "Import Files", icon: Upload },
    { to: "/distributor/reports", label: "Reports", icon: BarChart3 },
    { divider: true, label: "MF Research" },
    { to: "/top-performers", label: "Top Performers", icon: Award },
    { to: "/screener", label: "Fund Screener", icon: Filter },
    { to: "/consistent-performers", label: "Consistent Performers", icon: LineChart },
    { to: "/returns-calculator", label: "Returns Calculator", icon: BarChart3 },
    { to: "/point-to-point", label: "Point-to-Point", icon: Calendar },
    { to: "/compare", label: "Compare Funds", icon: TrendingUp },
    { to: "/fd-vs-debt", label: "FD vs Debt", icon: Landmark },
    { to: "/amc-aum", label: "AMC AUM Data", icon: FileText },
    { to: "/research", label: "Search Funds", icon: Search },
    { divider: true, label: "Tools & More" },
    { to: "/calculators", label: "Calculators", icon: Calculator },
    { to: "/blog", label: "Learn", icon: BookOpen },
    { to: "/admin", label: "Admin", icon: Shield },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full glass border-r border-white/5 transition-all duration-300 z-40 ${
          collapsed ? "w-20" : "w-64"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-white/5">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                <span className="text-primary-foreground font-bold text-lg font-serif">P</span>
              </div>
              {!collapsed && (
                <span className="font-serif text-lg font-semibold">Paisa Secure</span>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-3">
              {menuItems.map((item, index) => {
                // Handle divider
                if (item.divider) {
                  return (
                    <div key={`divider-${index}`} className="pt-4 pb-2">
                      {!collapsed && (
                        <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {item.label}
                        </span>
                      )}
                      {collapsed && <div className="border-t border-border/50 mx-2" />}
                    </div>
                  );
                }
                
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      isActive(item.to)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`}
                    data-testid={`sidebar-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-white/5">
            {!collapsed && user && (
              <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-secondary/50">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-foreground" />
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              className={`w-full justify-start text-muted-foreground hover:text-destructive ${
                collapsed ? "px-3" : ""
              }`}
              onClick={handleLogout}
              data-testid="sidebar-logout"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {!collapsed && <span className="ml-3">Logout</span>}
            </Button>
          </div>

          {/* Collapse Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute -right-4 top-20 w-8 h-8 rounded-full bg-card border border-border"
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 ${
          collapsed ? "ml-20" : "ml-64"
        }`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 glass border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-xl font-semibold">
                {menuItems.find(item => isActive(item.to))?.label || "Dashboard"}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
              </Button>
              <Button variant="ghost" size="icon" data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};
